import type { IconName } from "@/components/ui/icon";

/**
 * Shared source of truth for the job view tabs (Pipeline / Arbitrator /
 * Outreach Sequences / Tasks / Scorecards / Sourcing Agent). Each tab is its own route —
 * `/jobs/{jobId}/{tab}` — consumed by both the on-page `JobNavigationTabs`
 * strip (desktop, `lg` and up) and the mobile drawer's `JobPanel`
 * drill-down, so the two surfaces never drift.
 */
export const JOB_TAB_VALUES = [
  "pipeline",
  "arbitrator",
  "sequences",
  "tasks",
  "conversations",
  "scorecards",
  "agent",
] as const;

export type JobTabValue = (typeof JOB_TAB_VALUES)[number];

export interface JobTabItem {
  value: JobTabValue;
  label: string;
  icon: IconName;
  /** Not yet shipped — rendered non-interactively (Sourcing Agent). */
  comingSoon?: boolean;
}

export const JOB_TAB_ITEMS: JobTabItem[] = [
  { value: "pipeline", label: "Pipeline", icon: "IconTeam" },
  { value: "arbitrator", label: "Arbitrator", icon: "IconListSparkle" },
  { value: "sequences", label: "Outreach Sequences", icon: "IconPaperPlane" },
  { value: "tasks", label: "Tasks", icon: "IconTodos" },
  { value: "conversations", label: "Meetings", icon: "IconVideo" },
  { value: "scorecards", label: "Scorecards", icon: "IconChecklist" },
  {
    value: "agent",
    label: "Sourcing Agent",
    icon: "IconImagineAi",
    comingSoon: true,
  },
];

export interface JobRouteMatch {
  jobId: string;
  activeTab: JobTabValue;
}

const JOB_ROUTE_RE = new RegExp(
  `^/jobs/([^/]+)/(${JOB_TAB_VALUES.join("|")})(?:/|$)`,
);

/**
 * Matches a pathname against a job view tab route (`/jobs/{jobId}/{tab}`)
 * and extracts the job id and active tab. Matches deeper paths too — a
 * sequence-detail route (`/jobs/{jobId}/sequences/{sequenceId}/{tab}`)
 * matches with `activeTab: "sequences"`, and a candidate profile under
 * `/pipeline` matches with `activeTab: "pipeline"`. Returns `null` for
 * everything else, including the bare `/jobs` list route (which has no
 * tab segment).
 */
export function matchJobRoute(
  pathname: string | null | undefined,
): JobRouteMatch | null {
  if (!pathname) return null;
  const match = JOB_ROUTE_RE.exec(pathname);
  if (!match) return null;
  const [, jobId, activeTab] = match;
  return { jobId, activeTab: activeTab as JobTabValue };
}
